/**
 * Der Feedback-Reiter: Wünsche, Fehler, Lob — die Eingabebox der Kammer.
 */
import { useState, type FormEvent } from 'react'
import { api, ApiFehler } from '../api/client.js'
import { formatDatumZeit } from '../api/format.js'
import { useLaden } from '../api/laden.js'
import type { FeedbackEintrag } from '../api/typen.js'
import {
  Abschnitt,
  Amtsknopf,
  eingabeKlasse,
  Fehlermeldung,
  Laufvermerk,
  LeererZustand,
} from '../bausteine/Bausteine.js'
import { SpendenTeller } from '../bausteine/Spenden.js'

const KATEGORIEN = [
  ['wunsch', 'Wunsch', 'Ein Feature, das der Runde fehlt.'],
  ['fehler', 'Fehler', 'Etwas verhält sich nicht wie beschlossen.'],
  ['lob', 'Lob', 'Auch das nimmt die Kammer zu Protokoll.'],
] as const

export function Feedback() {
  const eintraege = useLaden(() => api.get<FeedbackEintrag[]>('/api/feedback'))
  const [kategorie, setKategorie] = useState<'wunsch' | 'fehler' | 'lob'>('wunsch')
  const [text, setText] = useState('')
  const [fehler, setFehler] = useState<string | null>(null)
  const [laeuft, setLaeuft] = useState(false)

  async function einreichen(e: FormEvent) {
    e.preventDefault()
    setLaeuft(true)
    setFehler(null)
    try {
      await api.post('/api/feedback', { kategorie, text })
      setText('')
      eintraege.neuLaden()
    } catch (f) {
      setFehler(f instanceof ApiFehler ? f.message : 'Die Eingabe konnte nicht zugestellt werden.')
    } finally {
      setLaeuft(false)
    }
  }

  return (
    <>
      <section className="pt-8">
        <h1 className="text-2xl">Eingaben an die Kammer</h1>
        <p className="mt-2 text-sm text-akte/75">
          Wünsche, Fehler, Lob — was die Runde bewegt, kommt hier zu Protokoll.
        </p>
      </section>

      <form onSubmit={einreichen} className="mt-6 flex flex-col gap-3">
        <div className="flex gap-1.5">
          {KATEGORIEN.map(([wert, label]) => (
            <button
              key={wert}
              type="button"
              onClick={() => setKategorie(wert)}
              aria-pressed={kategorie === wert}
              className={`flex-1 border px-3 py-2 text-sm transition-colors ${
                kategorie === wert
                  ? 'border-messing bg-messing/15 text-lampe'
                  : 'border-eiche text-akte/80 hover:border-messing/50'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={4}
          maxLength={1000}
          placeholder={KATEGORIEN.find(([w]) => w === kategorie)?.[2]}
          className={`w-full resize-y ${eingabeKlasse}`}
        />
        <Amtsknopf type="submit" disabled={laeuft || text.trim().length < 3}>
          {laeuft ? 'Wird zugestellt …' : 'Zu Protokoll geben'}
        </Amtsknopf>
        <Fehlermeldung text={fehler} />
      </form>

      <Abschnitt titel="Bisherige Eingaben">
        {eintraege.laedt ? (
          <Laufvermerk />
        ) : (eintraege.daten ?? []).length === 0 ? (
          <LeererZustand text="Noch keine Eingaben. Die Kammer hört zu." />
        ) : (
          <div className="flex flex-col gap-2">
            {(eintraege.daten ?? []).map((e) => (
              <div key={e.id} className="border border-eiche bg-bank px-3.5 py-2.5">
                <div className="flex items-baseline justify-between gap-3">
                  <span className="text-sm text-lampe">
                    {e.avatarEmoji} {e.name}
                  </span>
                  <span className="protokoll shrink-0">{e.kategorie}</span>
                </div>
                <p className="mt-1 text-sm text-akte/85">{e.text}</p>
                <p className="protokoll mt-1.5 normal-case">{formatDatumZeit(e.createdAt)}</p>
              </div>
            ))}
          </div>
        )}
      </Abschnitt>
      <SpendenTeller />
    </>
  )
}
